import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorBox, type Row } from '../components/DataTable';
import Modal from '../components/Modal';
import { api } from '../lib/api';
import { DISPATCH_PATH } from '../lib/pageTabs';
import RecvPane from './RecvPane';
import RecvForm from './RecvForm';
import DisptForm from './DisptForm';

/**
 * 일반 접수의 「노선·기사 편집」(게시판 #520 — 통근엔 있는데 일반엔 없었다).
 *
 * 일반 정산은 배차(노선·배정)에서 바로 나온다(통근처럼 달별 정산 줄이 따로 없다) → 편집 = 배차 편집.
 * 그래서 새 편집기를 만들지 않고 **접수·배차 화면 오른쪽 패널(`RecvPane`)을 그대로** 띄운다 —
 * 노선 추가·수정·복사, 노선을 누르면 차량·기사 배정. 서버·권한·마감 기한 가드도 배차 화면과 같다.
 * 접수 복사는 정산 화면에서 쓸 일이 아니라 뺐다.
 */
export default function GeneralRouteEditor({
  recvId,
  canWrite,
  onClose,
}: {
  recvId: number;
  canWrite: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [recv, setRecv] = useState<Row | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [editRecv, setEditRecv] = useState<number | null>(null);
  const [disptForm, setDisptForm] = useState<{ recvId: number; disptId?: number; copyFromId?: number } | null>(null);

  // 접수 한 줄 — 배차 화면 목록과 같은 칸(노선 수·담당 등)이 필요해 목록 API 를 번호로 부른다.
  // 검색어가 번호면 서버가 접수 NO 일치도 함께 보므로, 거래처명에 숫자가 섞여 걸린 줄은 여기서 거른다.
  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .listRecvs({ q: String(recvId), limit: 50 })
      .then((r) => {
        if (!alive) return;
        const hit = (r.rows as unknown as Row[]).find((x) => Number(x.recv_id) === recvId) ?? null;
        setRecv(hit);
        if (!hit) setError(`접수 ${recvId}번을 볼 수 없습니다(담당 범위 밖이거나 삭제된 접수).`);
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [recvId, nonce]);

  function changed(msg: string) {
    setNotice(msg);
    setNonce((n) => n + 1);
  }

  return (
    <div className="card space-y-3 p-4">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-bold">노선·기사 편집</h2>
        <span className="text-xs text-ink-soft">
          일반 배차는 노선·배정이 곧 정산입니다 — 접수·배차 화면과 같은 창입니다.
        </span>
        <button className="btn-ghost ml-auto" data-modal-close onClick={onClose}>
          닫기
        </button>
      </div>
      {notice && <div className="rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-700">{notice}</div>}
      {error && <ErrorBox message={error} />}
      {recv ? (
        <RecvPane
          recv={recv}
          canWrite={canWrite}
          nonce={nonce}
          onEditRecv={setEditRecv}
          onAddRoute={(id) => setDisptForm({ recvId: id })}
          onSeeAllRoutes={(id, label) => navigate(`${DISPATCH_PATH}?tab=dispt&recv=${id}&rl=${encodeURIComponent(label)}`)}
          onEditRoute={(rid, did) => setDisptForm({ recvId: rid, disptId: did })}
          onCopyRoute={(rid, did) => setDisptForm({ recvId: rid, copyFromId: did })}
          onChanged={changed}
        />
      ) : (
        !error && <div className="text-sm text-ink-faint">불러오는 중…</div>
      )}

      {editRecv !== null && (
        <Modal>
          <RecvForm
            recvId={editRecv}
            onClose={() => setEditRecv(null)}
            onSaved={(m) => {
              setEditRecv(null);
              changed(m);
            }}
          />
        </Modal>
      )}
      {disptForm && (
        <Modal width="max-w-4xl">
          <DisptForm
            recvId={disptForm.recvId}
            disptId={disptForm.disptId}
            copyFromId={disptForm.copyFromId}
            onClose={() => setDisptForm(null)}
            onSaved={(m) => {
              setDisptForm(null);
              changed(m);
            }}
          />
        </Modal>
      )}
    </div>
  );
}
